<template>
  <div class="energy-viz">
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>E = E_p + E_k = const$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
            <radialGradient id="ballGrad">
              <stop offset="0%" stop-color="#fff" />
              <stop offset="100%" stop-color="#00d2ff" />
            </radialGradient>
            <marker id="arrow-v" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
              <polygon points="0 0, 8 3, 0 6" fill="#2ed573" />
            </marker>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          <!-- 轨道 -->
          <path d="M 50 60 Q 200 220 350 60" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="4" stroke-linecap="round" />
          
          <!-- 小球轨迹阴影 -->
          <circle :cx="ballPos.x" :cy="ballPos.y" r="14" fill="rgba(0,210,255,0.1)" filter="url(#glow)" />
          
          <!-- 小球 -->
          <circle :cx="ballPos.x" :cy="ballPos.y" r="10" fill="url(#ballGrad)" />
          
          <!-- 速度矢量 -->
          <line v-if="Ek > 5" :x1="ballPos.x" :y1="ballPos.y" :x2="ballPos.x + vVec.x" :y2="ballPos.y + vVec.y" stroke="#2ed573" stroke-width="3" marker-end="url(#arrow-v)" />
        </svg>

        <div class="hud-top">
           <div class="energy-bars">
              <div class="bar-item">
                 <label>势能 Ep</label>
                 <div class="bar-bg"><div class="bar internal potential" :style="{ height: (Ep/totalE * 100) + '%' }"></div></div>
              </div>
              <div class="bar-item">
                 <label>动能 Ek</label>
                 <div class="bar-bg"><div class="bar internal kinetic" :style="{ height: (Ek/totalE * 100) + '%' }"></div></div>
              </div>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
           <div class="row items-center justify-between">
              <div class="stat-group">
                 <span class="label">总能量 E:</span>
                 <span class="value">{{ totalE.toFixed(0) }} J</span>
              </div>
              <el-button type="primary" size="mini" icon="el-icon-refresh" @click="reset">重置模拟</el-button>
           </div>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>物理详解：</strong>在不计摩擦的情况下，小球在最低点时**动能**最大，势能最小；在最高点时**势能**最大，动能为零。两者的总和始终保持不变，这就是机械能守恒定律。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MechanicalEnergyViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      t: 0,
      running: true
    }
  },
  computed: {
    ballPos() {
      const progress = 0.5 + 0.45 * Math.sin(this.t * 1.5);
      const x = 50 + 300 * progress;
      // y = a(x-200)^2 + 200, when x=50, y=60 -> 60 = a(-150)^2 + 200 -> a = -140/22500
      const y = (140/22500) * Math.pow(x - 200, 2) + 60 + 100; 
      // Adjusting for a 240 height box. Vertex at y=200, ends at y=60.
      return { x, y: (140/22500) * Math.pow(x - 200, 2) + 60 };
    },
    Ep() { return (200 - this.ballPos.y); },
    Ek() { return 140 - this.Ep; },
    totalE() { return 140; },
    vVec() {
      const direction = Math.cos(this.t * 1.5) > 0 ? 1 : -1;
      const speed = Math.sqrt(Math.max(0, this.Ek)) * 5;
      const dx = 1;
      const dy = (140/22500) * 2 * (this.ballPos.x - 200);
      const mag = Math.sqrt(1 + dy*dy);
      return { x: direction * speed * (1/mag), y: direction * speed * (dy/mag) };
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { this.running = false; },
  methods: {
    animate() {
      if (!this.running) return;
      this.t += 0.02;
      requestAnimationFrame(this.animate);
    },
    reset() { this.t = 0; }
  }
}
</script>

<style scoped lang="scss">
.energy-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #010205; border-radius: 16px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.hud-top { position: absolute; top: 15px; right: 20px; }
.energy-bars { display: flex; gap: 15px; align-items: flex-start; }
.bar-item { 
  display: flex; flex-direction: column; align-items: center; gap: 5px;
  label { font-size: 0.6rem; color: #888; }
  .bar-bg { width: 12px; height: 100px; background: rgba(255,255,255,0.05); border-radius: 6px; overflow: hidden; position: relative; display: flex; align-items: flex-end; }
  .bar.internal { width: 100%; transition: height 0.05s linear; }
  .potential { background: #ff4757; box-shadow: 0 0 10px rgba(255, 71, 87, 0.4); }
  .kinetic { background: #2ed573; box-shadow: 0 0 10px rgba(46, 213, 115, 0.4); }
}
.bottom-controls { padding: 0 20px 20px; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.02); border-radius: 12px; padding: 12px 15px; }
.stat-group { .label { font-size: 0.75rem; color: #888; } .value { font-size: 1rem; color: #00d2ff; font-family: monospace; font-weight: bold; margin-left: 8px; } }
.discovery-note { background: rgba(46, 213, 115, 0.05); padding: 12px 18px; border-radius: 12px; border: 1px solid rgba(46, 213, 115, 0.1); 
  p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
